import { useState } from "react";
import { motion } from "framer-motion";
import {
  Code,
  Sparkles,
  RefreshCw,
  ArrowLeft,
  Copy,
  Check,
  FileText,
  Globe
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const MetaGenerator = () => {
  const [title, setTitle] = useState("");
  const [keyword, setKeyword] = useState("");
  const [contentSummary, setContentSummary] = useState("");
  const [brandName, setBrandName] = useState("Mix & Mingle");
  const [pageType, setPageType] = useState("page");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedText, setCopiedText] = useState(null);

  const handleGenerate = async () => {
    if (!title.trim() || !keyword.trim()) return;

    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/admin/seo/generate-meta`, {
        title,
        keyword,
        content_summary: contentSummary,
        brand_name: brandName,
        page_type: pageType
      });
      setResults(res.data);
    } catch (err) {
      console.error("Error generating meta tags:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const MetaOption = ({ item, type }) => (
    <div className="p-4 border border-[rgba(212,175,55,0.15)] rounded-lg bg-[#121212] hover:border-[rgba(212,175,55,0.3)] transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-[#F5F5F0] mb-2">{type === 'title' ? item.title : item.description}</p>
          <div className="flex items-center gap-4 text-xs text-[#A1A1AA]">
            <span>{item.character_count} characters</span>
            {type === 'title' && item.keyword_position && (
              <span>Keyword: {item.keyword_position}</span>
            )}
            {type === 'description' && (
              <span>{item.includes_cta ? '✓ Has CTA' : 'No CTA'}</span>
            )}
          </div>
          {item.appeal && (
            <p className="text-[#a1a1aa] text-xs mt-2 italic">{item.appeal}</p>
          )}
        </div>
        <button
          onClick={() => copyToClipboard(type === 'title' ? item.title : item.description)}
          className="p-2 border border-[rgba(212,175,55,0.2)] rounded hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
        >
          {copiedText === (type === 'title' ? item.title : item.description) ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-[#D4AF37]" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/seo"
            className="p-2 border border-[rgba(212,175,55,0.2)] rounded hover:border-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[#D4AF37]" />
          </Link>
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] flex items-center gap-3">
              <Code className="w-8 h-8 text-purple-400" />
              Meta Tag Generator
            </h1>
            <p className="text-[#A1A1AA] mt-1">
              AI-powered SEO title and meta description generator
            </p>
          </div>
        </div>

        {/* Input Form */}
        <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Page Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., About Us"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Target Keyword *</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g., women's event community"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-[#A1A1AA] text-sm mb-2">Content Summary</label>
            <textarea
              value={contentSummary}
              onChange={(e) => setContentSummary(e.target.value)}
              placeholder="Brief description of what this page is about..."
              rows={3}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Brand Name</label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Your brand name"
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Page Type</label>
              <select
                value={pageType}
                onChange={(e) => setPageType(e.target.value)}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.2)] text-[#F5F5F0] p-3 rounded focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="page">Regular Page</option>
                <option value="homepage">Homepage</option>
                <option value="service">Service Page</option>
                <option value="product">Product Page</option>
                <option value="blog">Blog Post</option>
                <option value="about">About Page</option>
                <option value="contact">Contact Page</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading || !title.trim() || !keyword.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#050505] font-semibold rounded hover:bg-[#B8962E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Meta Tags
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {results && !results.error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Recommended */}
            {(results.recommended_title || results.recommended_description) && (
              <div className="border border-[#D4AF37] rounded-lg bg-[#D4AF37]/5 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="text-lg font-semibold text-[#F5F5F0]">AI Recommended</h3>
                </div>
                
                {results.recommended_title && (
                  <div className="mb-4">
                    <label className="text-[#A1A1AA] text-sm">Best Title:</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-[#F5F5F0] text-lg">{results.recommended_title}</p>
                      <button
                        onClick={() => copyToClipboard(results.recommended_title)}
                        className="p-1.5 hover:bg-[#D4AF37]/20 rounded transition-colors"
                      >
                        {copiedText === results.recommended_title ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-[#D4AF37]" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                
                {results.recommended_description && (
                  <div>
                    <label className="text-[#A1A1AA] text-sm">Best Description:</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-[#F5F5F0]">{results.recommended_description}</p>
                      <button
                        onClick={() => copyToClipboard(results.recommended_description)}
                        className="p-1.5 hover:bg-[#D4AF37]/20 rounded transition-colors flex-shrink-0"
                      >
                        {copiedText === results.recommended_description ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-[#D4AF37]" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Title Options */}
            {results.meta_titles?.length > 0 && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-semibold text-[#F5F5F0]">Title Options</h3>
                </div>
                <div className="space-y-3">
                  {results.meta_titles.map((item, i) => (
                    <MetaOption key={i} item={item} type="title" />
                  ))}
                </div>
              </div>
            )}

            {/* Description Options */}
            {results.meta_descriptions?.length > 0 && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-[#F5F5F0]">Description Options</h3>
                </div>
                <div className="space-y-3">
                  {results.meta_descriptions.map((item, i) => (
                    <MetaOption key={i} item={item} type="description" />
                  ))}
                </div>
              </div>
            )}

            {/* Social Media Tags */}
            {(results.og_title || results.twitter_title) && (
              <div className="border border-[rgba(212,175,55,0.2)] rounded-lg bg-[#0A0A0A] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-semibold text-[#F5F5F0]">Social Media Tags</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.og_title && (
                    <div className="p-4 bg-[#121212] rounded-lg">
                      <label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Open Graph Title</label>
                      <p className="text-[#F5F5F0] mt-1">{results.og_title}</p>
                    </div>
                  )}
                  {results.og_description && (
                    <div className="p-4 bg-[#121212] rounded-lg">
                      <label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Open Graph Description</label>
                      <p className="text-[#F5F5F0] mt-1">{results.og_description}</p>
                    </div>
                  )}
                  {results.twitter_title && (
                    <div className="p-4 bg-[#121212] rounded-lg">
                      <label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Twitter Card Title</label>
                      <p className="text-[#F5F5F0] mt-1">{results.twitter_title}</p>
                    </div>
                  )}
                  {results.twitter_description && (
                    <div className="p-4 bg-[#121212] rounded-lg">
                      <label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Twitter Card Description</label>
                      <p className="text-[#F5F5F0] mt-1">{results.twitter_description}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Error State */}
        {results?.error && (
          <div className="border border-red-500/30 rounded-lg bg-red-500/10 p-6">
            <p className="text-red-400">{results.error}</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MetaGenerator;
